import React from 'react';

interface PayoutDetailsModalProps {
  isOpen: boolean;
  payout: {
    id: number;
    merchant_id: number;
    amount: number;
    currency: string;
    status: string;
    merchant_reference_number: string;
    viyona_transaction_id: string;
    bank_reference_number: string;
    account_name: string;
    account_number: string;
    ifsc_code: string;
    bank_name: string;
    bank_branch?: string;
    transfer_type: string;
    customer_email?: string;
    customer_phone?: string;
    remark?: string;
    error_message?: string;
    transaction_date?: string;
    created_at: string;
    updated_at: string;
  } | null;
  onClose: () => void;
}

const PayoutDetailsModal: React.FC<PayoutDetailsModalProps> = ({ isOpen, payout, onClose }) => {
  if (!isOpen || !payout) return null;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusBadgeClass = (status: string) => {
    const s = (status || '').toUpperCase();
    if (s === 'SUCCESS') return 'bg-emerald-100 text-emerald-700';
    if (s === 'PENDING') return 'bg-amber-100 text-amber-700';
    if (s === 'PROCESSING') return 'bg-blue-100 text-blue-700';
    if (s === 'FAILED') return 'bg-red-100 text-red-700';
    if (s === 'CANCELLED') return 'bg-slate-100 text-slate-700';
    return 'bg-amber-100 text-amber-700';
  };

  const DetailSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="mb-6">
      <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider m-0 mb-3">{title}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );
  const DetailItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="flex flex-col gap-0.5">
      <label className="text-xs font-semibold text-slate-500">{label}</label>
      <span className="text-sm text-slate-800">{value}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-800 m-0">Payout Details</h2>
          <button type="button" className="w-10 h-10 flex items-center justify-center rounded-lg border-none text-slate-500 hover:bg-slate-100 cursor-pointer text-xl" onClick={onClose}>✕</button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          <DetailSection title="Basic Information">
            <DetailItem label="Payout ID" value={`#${payout.id}`} />
            <DetailItem label="Merchant ID" value={`#${payout.merchant_id}`} />
            <DetailItem label="Status" value={<span className={`inline-block py-1 px-2 rounded-full text-xs font-semibold uppercase ${getStatusBadgeClass(payout.status)}`}>{payout.status}</span>} />
            <DetailItem label="Amount" value={<span className="font-bold text-slate-800">{formatCurrency(payout.amount)}</span>} />
          </DetailSection>
          <DetailSection title="Transaction Information">
            <DetailItem label="Merchant Reference" value={<span className="font-mono text-xs">{payout.merchant_reference_number || 'N/A'}</span>} />
            <DetailItem label="YuvanAPI Transaction ID" value={<span className="font-mono text-xs">{payout.viyona_transaction_id || 'N/A'}</span>} />
            <DetailItem label="Bank Reference" value={<span className="font-mono text-xs">{payout.bank_reference_number || 'N/A'}</span>} />
            <DetailItem label="Transfer Type" value={payout.transfer_type || 'N/A'} />
          </DetailSection>
          <DetailSection title="Beneficiary Information">
            <DetailItem label="Account Holder Name" value={payout.account_name || 'N/A'} />
            <DetailItem label="Account Number" value={<span className="font-mono text-xs">{payout.account_number || 'N/A'}</span>} />
            <DetailItem label="IFSC Code" value={<span className="font-mono text-xs">{payout.ifsc_code || 'N/A'}</span>} />
            <DetailItem label="Bank Name" value={payout.bank_name || 'N/A'} />
            <DetailItem label="Bank Branch" value={payout.bank_branch || 'N/A'} />
          </DetailSection>
          <DetailSection title="Contact Information">
            <DetailItem label="Customer Email" value={payout.customer_email || 'N/A'} />
            <DetailItem label="Customer Phone" value={payout.customer_phone || 'N/A'} />
            <DetailItem label="Remark" value={payout.remark || 'N/A'} />
          </DetailSection>
          {payout.status === 'FAILED' && payout.error_message && (
            <div className="mb-6 p-4 rounded-xl border-2 border-red-200 bg-red-50">
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider m-0 mb-2">Error Information</h3>
              <p className="text-sm text-red-700 m-0">{payout.error_message}</p>
            </div>
          )}
          <DetailSection title="Timestamps">
            <DetailItem label="Created At" value={formatDate(payout.created_at)} />
            <DetailItem label="Updated At" value={formatDate(payout.updated_at)} />
            {payout.transaction_date && <DetailItem label="Transaction Date" value={formatDate(payout.transaction_date)} />}
          </DetailSection>
        </div>

        <div className="p-5 border-t border-slate-200">
          <button type="button" onClick={onClose} className="w-full py-2.5 px-5 rounded-xl bg-violet-600 text-white font-semibold text-sm cursor-pointer hover:bg-violet-700">Close</button>
        </div>
      </div>
    </div>
  );
};

export default PayoutDetailsModal;
